import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { MutableRefObject } from 'react';
import type { Editor as TinyEditor } from 'tinymce';

import { useDeleteComment } from '@/hooks/mutations/comment/useDeleteComment';
import { useInsertComment } from '@/hooks/mutations/comment/useInsertComment';
import { useUpdateComment } from '@/hooks/mutations/comment/useUpdateComment';
import { useDeleteConversation } from '@/hooks/mutations/conversation/useDeleteConversation';
import { useDeleteConversations } from '@/hooks/mutations/conversation/useDeleteConversations';
import { useInsertConversation } from '@/hooks/mutations/conversation/useInsertConversation';
import { useResolveConversation } from '@/hooks/mutations/conversation/useResolveConversation';
import { useGetCommentsByConversationId } from '@/hooks/queries';
import { isPermissionError } from '@/utils/graphqlUtils';
import { isTrpcPermissionError } from '@/utils/trpcUtils';

import type {
  ConversationLookupResponse,
  ConversationRequest,
  CreateRequest,
  CreateResponse,
  DeleteCommentRequest,
  DeleteResponse,
  DoneFunc,
  EditCommentRequest,
  EditCommentResponse,
  FailFunc,
  ReplyRequest,
  ReplyResponse,
  ResolveRequest,
  ResolveResponse,
} from './TinyTypes';

export const useComments = (
  editorRef: MutableRefObject<null | TinyEditor> | undefined,
  { ParentId }: { ParentId?: string }
) => {
  const { user } = useRisksmartUser();

  const { insertConversation } = useInsertConversation();
  const { insertComment } = useInsertComment();
  const { deleteComment } = useDeleteComment();
  const { resolveConversation } = useResolveConversation();
  const { deleteConversation } = useDeleteConversation();
  const { deleteConversations } = useDeleteConversations();
  const { updateComment } = useUpdateComment();
  const { getComments } = useGetCommentsByConversationId();

  return {
    tinycomments_mode: 'callback',

    tinycomments_create: async (
      req: CreateRequest,
      done: DoneFunc<CreateResponse>,
      fail: FailFunc
    ) => {
      if (!ParentId) {
        return;
      }
      try {
        const result = await insertConversation({
          Content: req.content,
          ParentId,
        });
        const response = {
          conversationUid: result.Id,
        };
        done(response);
      } catch (e) {
        fail(e);
      }
    },

    tinycomments_reply: async (
      req: ReplyRequest,
      done: DoneFunc<ReplyResponse>,
      fail: FailFunc
    ) => {
      try {
        const response = await insertComment({
          Content: req.content,
          ConversationId: req.conversationUid,
        });
        done({
          commentUid: response.Id,
        });
      } catch (e) {
        fail(e);
      }
    },

    tinycomments_edit_comment: async (
      req: EditCommentRequest,
      done: DoneFunc<EditCommentResponse>,
      fail: FailFunc
    ) => {
      try {
        await updateComment({
          Id: req.commentUid,
          Content: req.content,
        });
        done({
          canEdit: true,
        });
      } catch (e) {
        // A non-author edit is denied server-side. The Apollo path surfaces
        // this as a GraphQL `permission-error`; the tRPC path as a `FORBIDDEN`
        // TRPCClientError. Treat EITHER shape as "this user may not edit" so
        // TinyMCE disables editing rather than showing a hard failure.
        if (isPermissionError(e) || isTrpcPermissionError(e)) {
          done({
            canEdit: false,
          });

          return;
        }

        fail(e);
      }
    },

    tinycomments_delete: async (
      req: ConversationRequest,
      done: DoneFunc<DeleteResponse>,
      fail: FailFunc
    ) => {
      try {
        await deleteConversation({
          Id: req.conversationUid,
        });
        done({ canDelete: true });
      } catch (ex) {
        fail(ex);
      }
    },
    tinycomments_resolve: async (
      req: ResolveRequest,
      done: DoneFunc<ResolveResponse>,
      fail: FailFunc
    ) => {
      try {
        await resolveConversation({
          Id: req.conversationUid,
        });
        done({ canResolve: true });
      } catch (e) {
        fail(e);
      }
    },

    tinycomments_delete_all: async (
      req: unknown,
      done: DoneFunc<DeleteResponse>,
      fail: FailFunc
    ) => {
      if (!editorRef?.current) {
        return;
      }
      const conversations = editorRef.current.annotator.getAll('tinycomments');
      const conversationIds = Object.entries(conversations).map(
        ([conversationId]) => conversationId
      );
      try {
        await deleteConversations({
          Ids: conversationIds,
        });
        done({ canDelete: true });
      } catch (e) {
        fail(e);
      }
    },

    tinycomments_delete_comment: async (
      req: DeleteCommentRequest,
      done: DoneFunc<DeleteResponse>,
      fail: FailFunc
    ) => {
      try {
        await deleteComment({
          Id: req.commentUid,
        });
        done({
          canDelete: true,
        });
      } catch (e) {
        fail(e);
      }
    },

    tinycomments_lookup: async (
      req: ConversationRequest,
      done: DoneFunc<ConversationLookupResponse>,
      fail: FailFunc
    ) => {
      try {
        const comments = await getComments(req.conversationUid);
        const response = {
          conversation: {
            uid: req.conversationUid,
            comments: comments.map((c) => ({
              author: c.CreatedByUser,
              authorName: c.createdByUser?.FriendlyName || '-',
              createdAt: c.CreatedAtTimestamp,
              content: c.Content,
              modifiedAt: c.ModifiedAtTimestamp,
              uid: c.Id,
            })),
          },
        };
        done(response);
      } catch (e) {
        fail(e);
      }
    },
    tinycomments_author: user?.userId,
    tinycomments_author_name: user?.claims_username,
  };
};
